/**
 * Bug reporting via the shared @haskytech/bug-reporter SDK.
 *
 * The SDK captures browser evidence (screenshot, console, network, DOM, recent
 * actions) and POSTs a structured report to the HaskyOS bug-ingest endpoint
 * (unauthenticated by design). We suppress the SDK's own floating button (hidden
 * in globals.css) and trigger its capture dialog from the sidebar "Report a bug"
 * item via `openBugReporter()`.
 *
 * The SDK is browser-only (its report button extends HTMLElement), so it's
 * dynamically imported inside `ensureBugReporter` — never at module top level —
 * to keep it out of the SSR pass.
 */

let state: { reporter: unknown } | null = null;
let initInFlight = false;

/** Mount the SDK once (client-only). Falls back to the shared HaskyOS ingest
 *  endpoint when VITE_BUG_INGEST_URL is unset. `actor` is attached to
 *  reports as user metadata (email / role). */
export async function ensureBugReporter(actor?: Record<string, unknown>): Promise<void> {
  if (state || initInFlight || typeof window === 'undefined') return;
  // HaskyOS bug-ingest (one shared prod store all apps post to — unauthenticated
  // + CORS-open). Overridable per-env via the build arg.
  const endpoint =
    import.meta.env.VITE_BUG_INGEST_URL || 'https://haskos.haskytech.com/api/v1/bugs/ingest';
  initInFlight = true;
  try {
    const { initBugReporter } = await import('@haskytech/bug-reporter');
    const reporter = initBugReporter({
      endpoint,
      workstream: 'yakson',
      userMeta: actor ?? {},
      metadata: () => ({
        route: window.location.pathname,
        appEnv: import.meta.env.VITE_APP_ENV ?? 'unknown',
      }),
    });
    state = { reporter };
  } catch (err) {
    console.error('[bug-reporter] init failed', err);
  } finally {
    initInFlight = false;
  }
}

/**
 * Open the SDK's capture dialog. We drive it from our own sidebar entry instead
 * of the SDK's floating button (which is hidden) by dispatching the same
 * `report-click` event the button uses — the reporter listens for it.
 */
export function openBugReporter(): void {
  if (typeof document === 'undefined') return;
  const button = document.querySelector('bug-reporter-button');
  if (button) {
    button.dispatchEvent(new CustomEvent('report-click'));
  } else {
    console.warn('[bug-reporter] not initialised (VITE_BUG_INGEST_URL set?)');
  }
}
